import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Book, ChevronLeft, ChevronRight, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Header from '../components/Header';
import Footer from '../components/Footer';
import TableOfContents from '../components/TableOfContents';
import { useAuth } from '../context/AuthContext';
import { chapters, canAccessChapter } from '../data/chapters';

// Import all chapter components
import Chapter1Content from './chapters/Chapter1Content';
import Chapter2Content from './chapters/Chapter2Content';
import Chapter3Content from './chapters/Chapter3Content';
import Chapter4Content from './chapters/Chapter4Content';
import Chapter5Content from './chapters/Chapter5Content';
import Chapter6Content from './chapters/Chapter6Content';
import Chapter7Content from './chapters/Chapter7Content';
import Chapter8Content from './chapters/Chapter8Content';
import ClosingChapterContent from './chapters/ClosingChapterContent';
import BackMatterContent from './chapters/BackMatterContent';

// Map slugs to chapter IDs and components
const slugToChapter = {
  'chapter-1': { id: 1, component: Chapter1Content },
  'chapter-2': { id: 2, component: Chapter2Content },
  'chapter-3': { id: 3, component: Chapter3Content },
  'chapter-4': { id: 4, component: Chapter4Content },
  'chapter-5': { id: 5, component: Chapter5Content },
  'chapter-6': { id: 6, component: Chapter6Content },
  'chapter-7': { id: 7, component: Chapter7Content },
  'chapter-8': { id: 8, component: Chapter8Content },
  'closing': { id: 'closing', component: ClosingChapterContent },
  'back-matter': { id: 'back-matter', component: BackMatterContent }
};

// Helper to get adjacent chapters
const getAdjacentChapters = (slug) => {
  const slugs = Object.keys(slugToChapter);
  const currentIndex = slugs.indexOf(slug);
  
  return {
    prev: currentIndex > 0 ? slugs[currentIndex - 1] : null,
    next: currentIndex < slugs.length - 1 ? slugs[currentIndex + 1] : null
  };
};

const EbookReader = () => {
  const navigate = useNavigate();
  const { chapterSlug } = useParams();
  const { user, isAuthenticated } = useAuth();
  const [showSidebar, setShowSidebar] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  // Default to chapter-1 if no slug provided
  const currentSlug = chapterSlug || 'chapter-1';
  const currentChapterData = slugToChapter[currentSlug];

  // Track scroll progress
  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight - windowHeight;
      const scrollTop = window.scrollY;
      const progress = (scrollTop / documentHeight) * 100;
      setScrollProgress(Math.min(progress, 100));
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top when chapter changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentSlug]);

  // Check if user has access to current chapter
  const hasAccess = () => {
    if (!isAuthenticated()) return false;
    const chapterId = currentChapterData?.id;
    if (typeof chapterId === 'string') return true; // Closing and back matter accessible to all logged-in users
    return canAccessChapter(chapterId, user?.subscription_tier);
  };

  // Access denial screen
  if (!isAuthenticated()) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-8 text-center">
            <Book className="h-16 w-16 text-amber-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-4">The King's Return</h2>
            <p className="text-gray-600 mb-6">
              Create a free Seeker account to read Chapter 1: The Four Corner Pieces
            </p>
            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/register')}
                className="w-full bg-amber-600 hover:bg-amber-700"
              >
                Create Free Account
              </Button>
              <Button 
                onClick={() => navigate('/login')}
                variant="outline"
                className="w-full"
              >
                Sign In
              </Button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!hasAccess()) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-8 text-center">
            <Book className="h-16 w-16 text-amber-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-4">Upgrade to Continue Reading</h2>
            <p className="text-gray-600 mb-6">
              This chapter requires a Berean tier subscription or higher.
            </p>
            <Button 
              onClick={() => navigate('/subscriptions')}
              className="w-full bg-amber-600 hover:bg-amber-700"
            >
              View Subscription Options
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const { prev, next } = getAdjacentChapters(currentSlug);
  const ChapterComponent = currentChapterData?.component;
  const chapterInfo = Object.values(chapters).find(ch => ch.slug === currentSlug);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Progress Bar */}
      <div className="fixed top-16 left-0 right-0 h-1 bg-gray-200 z-40">
        <div 
          className="h-full bg-amber-600 transition-all duration-300"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      {/* Ebook Header */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white py-8 border-b">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <Badge className="bg-amber-600">Little Book Series - Book 2</Badge>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSidebar(!showSidebar)}
                className="text-white hover:bg-slate-700"
              >
                {showSidebar ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                <span className="ml-2">Contents</span>
              </Button>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2">THE KING'S RETURN</h1>
            <p className="text-gray-300 text-lg">The Legend — Reading the Treasure Map Together</p>
            <p className="text-sm text-gray-400 mt-2">by Curtis Alexander Fenison ("Mordecai")</p>
          </div>
        </div>
      </div>

      {/* Sidebar */}
      {showSidebar && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50" onClick={() => setShowSidebar(false)}>
          <div className="fixed left-0 top-0 bottom-0 w-80 bg-white shadow-xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold">Table of Contents</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowSidebar(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
              <TableOfContents 
                currentChapter={currentChapterData?.id} 
                userTier={user?.subscription_tier}
                onClose={() => setShowSidebar(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Chapter Navigation */}
          <div className="flex items-center justify-between mb-8 pb-4 border-b">
            <Button 
              variant="ghost" 
              size="sm" 
              disabled={!prev}
              onClick={() => prev && navigate(`/ebook/${prev}`)}
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Previous
            </Button>
            <div className="text-center">
              <h2 className="text-xl font-bold">{chapterInfo?.title}</h2>
              {chapterInfo?.subtitle && (
                <p className="text-sm text-gray-600">{chapterInfo.subtitle}</p>
              )}
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              disabled={!next}
              onClick={() => next && navigate(`/ebook/${next}`)}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>

          {/* Chapter Content */}
          {ChapterComponent && <ChapterComponent />}

          {/* Bottom Navigation */}
          <div className="flex items-center justify-between mt-12 pt-8 border-t">
            <Button 
              variant="outline" 
              disabled={!prev}
              onClick={() => prev && navigate(`/ebook/${prev}`)}
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Previous Chapter
            </Button>
            <Button 
              variant="outline"
              disabled={!next}
              onClick={() => next && navigate(`/ebook/${next}`)}
            >
              Next Chapter
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default EbookReader;
